import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Search,
  Visibility,
  Edit,
  Description,
  Download
} from '@mui/icons-material';
import api from '../services/api';

const Contracts = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contracts');
      setContracts(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setError('Failed to load contracts');
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredContracts = contracts.filter(contract => {
    const searchLower = searchTerm.toLowerCase();
    const employeeName = `${contract.employee_first_name} ${contract.employee_last_name}`.toLowerCase();
    const contractNumber = contract.contract_number?.toLowerCase() || '';
    const position = contract.position_title?.toLowerCase() || '';
    
    return employeeName.includes(searchLower) || 
           contractNumber.includes(searchLower) ||
           position.includes(searchLower);
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'error';
      case 'terminated': return 'default';
      default: return 'default';
    }
  };

  const getContractStatus = (contract) => {
    if (contract.status === 'terminated') return 'terminated';
    if (!contract.signed_by_employee || !contract.signed_by_employer) return 'pending';
    
    const endDate = new Date(contract.end_date);
    const today = new Date();
    
    if (endDate < today) return 'expired';
    return 'active';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Contracts Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/admin/contracts/new')}
        >
          New Contract
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search contracts by employee name, contract number, or position..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Contract Number</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Salary</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Signed</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading contracts...
                </TableCell>
              </TableRow>
            ) : filteredContracts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {searchTerm ? 'No contracts found matching your search' : 'No contracts found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredContracts.map((contract) => {
                const status = getContractStatus(contract);
                return (
                  <TableRow key={contract.id} hover>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{ 
                          cursor: 'pointer', 
                          color: 'primary.main',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/admin/contracts/${contract.id}`)}
                      >
                        {contract.contract_number || `CNT-${contract.id}`}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/admin/employees/${contract.employee_id}`)}
                      >
                        {contract.employee_first_name} {contract.employee_last_name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {contract.position_title || 'N/A'}
                      {contract.position_title_lt && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {contract.position_title_lt}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(contract.start_date)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        to {formatDate(contract.end_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {contract.salary_amount} {contract.salary_currency || 'EUR'}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {contract.salary_type || 'monthly'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={status} 
                        color={getStatusColor(status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Chip
                          label={contract.signed_by_employee ? '✓ Employee' : '○ Employee'}
                          size="small"
                          color={contract.signed_by_employee ? 'success' : 'default'}
                          sx={{ mb: 0.5 }}
                        />
                        <Chip
                          label={contract.signed_by_employer ? '✓ Employer' : '○ Employer'}
                          size="small"
                          color={contract.signed_by_employer ? 'success' : 'default'}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/contracts/${contract.id}`)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/contracts/edit/${contract.id}`)}
                        title="Edit Contract"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {/* TODO: Generate PDF */}}
                        title="Download PDF"
                        disabled
                      >
                        <Download />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Statistics */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6">{contracts.filter(c => getContractStatus(c) === 'active').length}</Typography>
          <Typography variant="body2" color="text.secondary">Active Contracts</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6">{contracts.filter(c => getContractStatus(c) === 'pending').length}</Typography>
          <Typography variant="body2" color="text.secondary">Pending Signature</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6">{contracts.filter(c => getContractStatus(c) === 'expired').length}</Typography>
          <Typography variant="body2" color="text.secondary">Expired</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6">{contracts.length}</Typography>
          <Typography variant="body2" color="text.secondary">Total Contracts</Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default Contracts;
